// === ./src/frontend/components/StatsBar.jsx ===
import React, { useState, useEffect, useContext } from "react";
import { Box, Container, Typography, Grid, alpha } from "@mui/material";
import { TrendingUp, CloudDownload, Storage, Speed } from "@mui/icons-material";
import { waveBar } from "./animations";
import { TranslateContext } from "../../context/TranslateContext";

const StatsBar = ({ theme }) => {
  const { lang } = useContext(TranslateContext);
  const [counts, setCounts] = useState({
    users: 0,
    downloads: 0,
    storage: 0,
    uptime: 0,
  });

  useEffect(() => {
    const targets = {
      users: 24800,
      downloads: 1240000,
      storage: 98,
      uptime: 99.9,
    };
    const duration = 2000;
    const steps = 60;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const ease = 1 - Math.pow(1 - step / steps, 3);
      setCounts({
        users: Math.floor(targets.users * ease),
        downloads: Math.floor(targets.downloads * ease),
        storage: Math.floor(targets.storage * ease),
        uptime: +(targets.uptime * ease).toFixed(1),
      });
      if (step >= steps) clearInterval(timer);
    }, duration / steps);
    return () => clearInterval(timer);
  }, []);

  const stats = [
    {
      label: lang?.statsActiveUsers || "Active Users",
      value: counts.users.toLocaleString() + "+",
      icon: <TrendingUp />,
    },
    {
      label: lang?.statsFilesDownloaded || "Files Downloaded",
      value: counts.downloads.toLocaleString() + "+",
      icon: <CloudDownload />,
    },
    {
      label: lang?.statsStorageUptime || "Storage Uptime",
      value: counts.storage + "%",
      icon: <Storage />,
    },
    {
      label: lang?.statsServerUptime || "Server Uptime",
      value: counts.uptime + "%",
      icon: <Speed />,
    },
  ];

  return (
    <Box
      sx={{
        py: { xs: 6, md: 8 },
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.8)} 100%)`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Wave background */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.06,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 4,
        }}
      >
        {Array.from({ length: 40 }).map((_, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: `${30 + Math.random() * 60}%`,
              bgcolor: "#fff",
              borderRadius: 1,
              animation: `${waveBar} ${1 + Math.random()}s ease-in-out infinite`,
              animationDelay: `${i * 0.05}s`,
            }}
          />
        ))}
      </Box>
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={4}>
          {stats.map((s, i) => (
            <Grid size={{ xs: 6, md: 3 }} key={i}>
              <Box textAlign="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: alpha("#fff", 0.15),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  {React.cloneElement(s.icon, { sx: { fontSize: 22 } })}
                </Box>
                <Typography
                  variant="h3"
                  fontWeight={800}
                  color="#fff"
                  sx={{
                    fontSize: { xs: "1.8rem", md: "2.4rem" },
                    letterSpacing: "-0.03em",
                  }}
                >
                  {s.value}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: alpha("#fff", 0.75), fontWeight: 500 }}
                >
                  {s.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default StatsBar;
